import React, { useState } from "react";
import { useParams } from "react-router-dom";
import CustomRichTextEditor from "../../components/form/input/CustomRichTextEditor";
import { apiRequestWithAuth } from "../../utils/api";
import Alert from "../../components/ui/alert/Alert";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Button from "../../components/ui/button/Button";
import MyEditor5 from "../../components/form/input/MyEditor5";

type Question = {
    questionText: string;
};

type LessonFormData = {
    title: string;
    shortDescription: string;
    postContent: string;
    videoUrl: string;
    orderNo: number;
    attachmentId: string;
    questions: Question[];
};

const LessonCreatePage = () => {
    const { courseId } = useParams();
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [openQuestionModal, setOpenQuestionModal] = useState(false);
    const [newQuestion, setNewQuestion] = useState({ questionText: "" });

    const [formData, setFormData] = useState<LessonFormData>({
        title: "",
        shortDescription: "",
        postContent: "",
        videoUrl: "",
        orderNo: 1,
        attachmentId: "",
        questions: [],
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            await apiRequestWithAuth(`/course/${courseId}/lesson`, "POST", formData);
            setOpenSnackbar(true);
            setFormData({
                title: "",
                shortDescription: "",
                postContent: "",
                videoUrl: "",
                orderNo: 1,
                attachmentId: "",
                questions: [],
            });
        } catch (error) {
            alert("Lỗi khi tạo bài học");
        }
    };

    const handleAddQuestion = () => {
        if (!newQuestion.questionText.trim()) return;

        setFormData(prev => ({
            ...prev,
            questions: [...prev.questions, newQuestion],
        }));
        setNewQuestion({ questionText: "" });
        setOpenQuestionModal(false);
    };

    return (
        <div className="p-4 max-w-4xl mx-auto">
            {openSnackbar && (
                <Alert variant="success" title="Tạo bài học thành công" message="Bạn đã thêm bài học mới thành công." />
            )}

            <h2 className="text-2xl font-bold mb-6">Tạo bài học mới</h2>

            <ComponentCard title="Thông tin bài học">
                <div className="space-y-6">
                    <div>
                        <Label>Tiêu đề</Label>
                        <Input name="title" value={formData.title} onChange={handleChange} />
                    </div>

                    <div>
                        <Label>Mô tả ngắn</Label>
                        <Input name="shortDescription" value={formData.shortDescription} onChange={handleChange} />
                    </div>

                    <div>
                        <Label>Nội dung</Label>
                        <MyEditor5
                            content={formData.postContent}
                            onBlur={(value) => setFormData(prev => ({ ...prev, postContent: value }))}
                        />
                    </div>

                    <div>
                        <Label>Video URL</Label>
                        <Input name="videoUrl" value={formData.videoUrl} onChange={handleChange} />
                    </div>

                    <div>
                        <Label>Thứ tự</Label>
                        <Input type="number" name="orderNo" value={formData.orderNo} onChange={handleChange} />
                    </div>
                </div>
            </ComponentCard>

            <ComponentCard title="Câu hỏi kiểm tra">
                <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <Label>Danh sách câu hỏi</Label>
                        <Button onClick={() => setOpenQuestionModal(true)} className="bg-indigo-500 text-white px-3 py-1 rounded">
                            + Thêm câu hỏi
                        </Button>
                    </div>

                    {formData.questions.length === 0 && (
                        <p className="text-gray-400 italic">Chưa có câu hỏi nào.</p>
                    )}

                    {formData.questions.map((q: any, index) => (
                        <div key={index} className="p-3 border rounded mb-2 bg-gray-50">
                            <div className="font-medium">{q.questionText}</div>
                        </div>
                    ))}
                </div>
            </ComponentCard>

            <Button onClick={handleSubmit} className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded">
                Lưu bài học
            </Button>

            {/* Modal thêm câu hỏi (tuỳ chỉnh, không dùng material) */}
            {openQuestionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Thêm câu hỏi mới</h3>
                        <Input
                            placeholder="Nhập nội dung câu hỏi"
                            value={newQuestion.questionText}
                            onChange={(e) => setNewQuestion({ questionText: e.target.value })}
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <Button onClick={() => setOpenQuestionModal(false)} className="bg-gray-300 px-4 py-1 rounded">
                                Hủy
                            </Button>
                            <Button onClick={handleAddQuestion} className="bg-blue-500 text-white px-4 py-1 rounded">
                                Thêm
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LessonCreatePage;
